import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import { brandColors } from '../../../../theme/theme';
import { ETHNICITY_COLORS, getVelocityIndicator } from '../../../../lib/utils/deepDiveUtils';

const EthnicityTrendChart = ({ trendData }) => {
  const [selectedMetric, setSelectedMetric] = useState('awareness');
  const [selectedEthnicities, setSelectedEthnicities] = useState(Object.keys(ETHNICITY_COLORS));

  if (!trendData || Object.keys(trendData).length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No trend data available
        </Typography>
      </Paper>
    );
  }

  const metrics = [
    { key: 'awareness', label: 'Awareness' },
    { key: 'interest', label: 'Interest' },
    { key: 'theater', label: 'Theater Intent' },
    { key: 'payment', label: 'Willing to Pay' },
    { key: 'heat', label: 'Heat (7/7)' }
  ];

  // Prepare chart data
  const chartData = [];
  const maxWaves = Math.max(
    ...Object.values(trendData).map(trend => trend.data.length)
  );

  for (let i = 0; i < maxWaves; i++) {
    const dataPoint = { wave: i + 1 };
    
    selectedEthnicities.forEach(ethnicity => {
      if (trendData[ethnicity] && trendData[ethnicity].data[i]) {
        dataPoint[ethnicity] = trendData[ethnicity].data[i][selectedMetric];
      }
    });
    
    chartData.push(dataPoint);
  }

  const handleEthnicityToggle = (ethnicity) => {
    setSelectedEthnicities(prev => {
      if (prev.includes(ethnicity)) {
        return prev.filter(e => e !== ethnicity);
      } else {
        return [...prev, ethnicity];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedEthnicities.length === Object.keys(ETHNICITY_COLORS).length) {
      setSelectedEthnicities([]);
    } else {
      setSelectedEthnicities(Object.keys(ETHNICITY_COLORS));
    }
  };

  // Get velocity indicators for current metric
  const velocityMetricKey = `${selectedMetric}Velocity`;
  const velocities = {};
  Object.keys(trendData).forEach(ethnicity => {
    if (trendData[ethnicity][velocityMetricKey]) {
      velocities[ethnicity] = trendData[ethnicity][velocityMetricKey];
    }
  });

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Demographic Trends Over Time
      </Typography>

      <Grid container spacing={3}>
        {/* Metric Selection */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Select Metric:
            </Typography>
            <ToggleButtonGroup
              value={selectedMetric}
              exclusive
              onChange={(e, value) => value && setSelectedMetric(value)}
              size="small"
            >
              {metrics.map(metric => (
                <ToggleButton key={metric.key} value={metric.key}>
                  {metric.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Ethnicity Selection */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Show:
            </Typography>
            <Chip
              label="All"
              size="small"
              onClick={handleSelectAll}
              color={selectedEthnicities.length === Object.keys(ETHNICITY_COLORS).length ? 'primary' : 'default'}
            />
            {Object.keys(ETHNICITY_COLORS).map(ethnicity => (
              <FormControlLabel
                key={ethnicity}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedEthnicities.includes(ethnicity)}
                    onChange={() => handleEthnicityToggle(ethnicity)}
                    sx={{
                      color: ETHNICITY_COLORS[ethnicity],
                      '&.Mui-checked': {
                        color: ETHNICITY_COLORS[ethnicity]
                      }
                    }}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                    {ethnicity}
                  </Typography>
                }
              />
            ))}
          </Box>
        </Grid>

        {/* Trend Chart */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="wave"
                    label={{
                      value: 'Survey Wave (Oldest → Newest)',
                      position: 'bottom',
                      offset: 0,
                      style: { fontSize: '0.85rem', fontWeight: 600 }
                    }}
                  />
                  <YAxis
                    label={{
                      value: `${metrics.find(m => m.key === selectedMetric)?.label} %`,
                      angle: -90,
                      position: 'insideLeft',
                      style: { fontSize: '0.85rem', fontWeight: 600 }
                    }}
                    domain={[0, 100]}
                  />
                  <RechartsTooltip
                    formatter={(value) => `${Math.round(value)}%`}
                    labelFormatter={(label) => `Wave ${label}`}
                  />
                  <Legend />
                  {selectedEthnicities.map(ethnicity => (
                    <Line
                      key={ethnicity}
                      type="monotone"
                      dataKey={ethnicity}
                      stroke={ETHNICITY_COLORS[ethnicity]}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      name={ethnicity}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Velocity Indicators */}
        <Grid item xs={12} lg={4}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Trend Velocity ({metrics.find(m => m.key === selectedMetric)?.label})
          </Typography>
          <Grid container spacing={1}>
            {Object.keys(ETHNICITY_COLORS).map(ethnicity => {
              if (!velocities[ethnicity] || !selectedEthnicities.includes(ethnicity)) return null;
              
              const velocity = velocities[ethnicity];
              const indicator = getVelocityIndicator(velocity);
              
              return (
                <Grid item xs={12} key={ethnicity}>
                  <Card
                    sx={{
                      border: `2px solid ${ETHNICITY_COLORS[ethnicity]}40`,
                      backgroundColor: ETHNICITY_COLORS[ethnicity] + '05'
                    }}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              backgroundColor: ETHNICITY_COLORS[ethnicity]
                            }}
                          />
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {ethnicity}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              color: indicator.color,
                              fontSize: '1.2rem'
                            }}
                          >
                            {indicator.icon}
                          </Typography>
                          <Typography variant="caption" sx={{ color: indicator.color, fontWeight: 600 }}>
                            {indicator.text}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">
                          Latest Change:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: velocity.latestChange > 0 ? '#4caf50' : velocity.latestChange < 0 ? '#f44336' : '#757575'
                          }}
                        >
                          {velocity.latestChange > 0 ? '+' : ''}{Math.round(velocity.latestChange)}pp
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        {/* Insights */}
        <Grid item xs={12}>
          <Card sx={{ backgroundColor: brandColors.primary + '05' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Trend Insights:
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(velocities)
                  .filter(([ethnicity]) => selectedEthnicities.includes(ethnicity))
                  .sort((a, b) => Math.abs(b[1].velocity) - Math.abs(a[1].velocity))
                  .slice(0, 3)
                  .map(([ethnicity, velocity]) => {
                    const indicator = getVelocityIndicator(velocity);
                    let message = '';
                    
                    if (velocity.direction === 'increasing') {
                      message = `${ethnicity} ${selectedMetric} is growing at ${Math.abs(velocity.velocity).toFixed(1)}pp per wave`;
                    } else if (velocity.direction === 'decreasing') {
                      message = `${ethnicity} ${selectedMetric} is declining at ${Math.abs(velocity.velocity).toFixed(1)}pp per wave`;
                    } else {
                      message = `${ethnicity} ${selectedMetric} is stable`;
                    }

                    return (
                      <Grid item xs={12} sm={6} md={4} key={ethnicity}>
                        <Chip
                          icon={
                            velocity.direction === 'increasing' ? <TrendingUp /> :
                            velocity.direction === 'decreasing' ? <TrendingDown /> :
                            <TrendingFlat />
                          }
                          label={message}
                          size="small"
                          sx={{
                            width: '100%',
                            justifyContent: 'flex-start',
                            '& .MuiChip-label': { fontSize: '0.75rem' }
                          }}
                        />
                      </Grid>
                    );
                  })}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default EthnicityTrendChart;

